import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, useAuth } from '../App';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { formatDuration } from '../utils/format';
import { Calendar, Star, Clock, CheckCircle, XCircle, Heart, Eye, CreditCard, ShieldCheck, Sparkles } from 'lucide-react';
import Favourites from './Favourites';
import { ReviewDialog } from '../components/client/ReviewDialog';
import { ConsultationFormDialog } from '../components/client/ConsultationFormDialog';
import { BookingCard } from '../components/client/BookingCard';
import { ReferralCard } from '../components/client/ReferralCard';
import { PaymentMethodsSection } from '../components/client/PaymentMethodsSection';
import FeatureRequestsSection from '../components/feedback/FeatureRequestsSection';
import RecognisedDevicesCard from '../components/account/RecognisedDevicesCard';
import { DeleteAccountCard } from '../components/account/DeleteAccountCard';
import TwoFactorSettingsCard from '../components/account/TwoFactorSettingsCard';
import { NotificationPreferencesCard } from '../components/account/NotificationPreferencesCard';
import { useScrollActiveTabIntoView } from '../hooks/useScrollActiveTabIntoView';

export default function ClientDashboard() {
  const { user, betaConfig } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reportBookingParam = searchParams.get('report_booking');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const tabsScrollRef = useScrollActiveTabIntoView(activeTab);

  // Review form
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState('');

  // Consultation form
  const [consultationDialogOpen, setConsultationDialogOpen] = useState(false);
  const [consultationForm, setConsultationForm] = useState(null);
  const [consultationBooking, setConsultationBooking] = useState(null);
  const [formAnswers, setFormAnswers] = useState({});

  // View all limits
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const [showAllCompleted, setShowAllCompleted] = useState(false);
  const [showAllCancelled, setShowAllCancelled] = useState(false);
  const LIST_LIMIT = 10;

  // Recently viewed
  const [recentlyViewed, setRecentlyViewed] = useState([]);

  // Review eligibility cache
  const [reviewEligibility, setReviewEligibility] = useState({});

  useEffect(() => {
    // Re-verify session on dashboard mount
    const verifyAndFetch = async () => {
      try {
        await api.get('/auth/me');
        fetchBookings();
        fetchRecentlyViewed();
      } catch (err) {
        console.error('Client session verification failed:', err);
        navigate('/login', { replace: true });
      }
    };
    verifyAndFetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await api.get('/bookings');
      setBookings(response.data || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentlyViewed = async () => {
    try {
      const res = await api.get('/recently-viewed');
      setRecentlyViewed(res.data || []);
    } catch (err) { console.error('Failed to load recently viewed:', err); }
  };

  const handleReview = async () => {
    try {
      await api.post('/reviews', {
        booking_id: selectedBooking.booking_id,
        rating: reviewRating,
        text: reviewText,
        review_type: 'client_to_provider',
      });
      toast.success('Review submitted!');
      setReviewDialogOpen(false);
      setSelectedBooking(null);
      setReviewRating(5);
      setReviewText('');
      // Mark as submitted locally
      setReviewEligibility(prev => ({
        ...prev,
        [selectedBooking.booking_id]: { ...prev[selectedBooking.booking_id], can_review: false, client_review_submitted: true },
      }));
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  const checkEligibility = async (bookingId) => {
    if (reviewEligibility[bookingId]) return reviewEligibility[bookingId];
    try {
      const res = await api.get(`/reviews/check/${bookingId}`);
      setReviewEligibility(prev => ({ ...prev, [bookingId]: res.data }));
      return res.data;
    } catch {
      return { can_review: false, eligible: false };
    }
  };

  // Fetch eligibility for all completed bookings
  useEffect(() => {
    const completed = bookings.filter(b => b.status === 'completed');
    completed.forEach(b => checkEligibility(b.booking_id));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings]);

  const openConsultationForm = async (booking) => {
    if (!booking.consultation_form_id) return;
    try {
      const res = await api.get(`/consultation-forms/${booking.consultation_form_id}`);
      setConsultationForm(res.data);
      setConsultationBooking(booking);
      setFormAnswers({});
      setConsultationDialogOpen(true);
    } catch (err) {
      console.error('Consultation form load error:', err);
      toast.error('Could not load consultation form');
    }
  };

  const submitConsultation = async () => {
    if (!consultationForm || !consultationBooking) return;
    for (const q of consultationForm.questions) {
      if (q.required && (!formAnswers[q.question_id] || !formAnswers[q.question_id].toString().trim())) {
        toast.error(`Please answer: ${q.text}`);
        return;
      }
    }
    try {
      const answers = consultationForm.questions.map(q => ({
        question_id: q.question_id,
        question_text: q.text,
        answer: formAnswers[q.question_id] || ''
      }));
      await api.post('/consultation-responses', {
        booking_id: consultationBooking.booking_id,
        form_id: consultationForm.form_id,
        answers
      });
      toast.success('Consultation form submitted!');
      setConsultationDialogOpen(false);
      setBookings(prev => prev.map(b =>
        b.booking_id === consultationBooking.booking_id
          ? { ...b, consultation_submitted: true }
          : b
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    }
  };

  const now = new Date();
  const upcomingBookings = bookings.filter(b => {
    if (b.status === 'pending') return true;
    if (b.status === 'confirmed') {
      const bDate = new Date(`${b.date}T${b.time || '23:59'}:00`);
      return bDate > now;
    }
    return false;
  });
  const completedBookings = bookings.filter(b => {
    if (b.status === 'completed') return true;
    if (b.status === 'confirmed') {
      const bDate = new Date(`${b.date}T${b.time || '23:59'}:00`);
      return bDate <= now;
    }
    return false;
  });
  const cancelledBookings = bookings.filter(b => b.status === 'cancelled');

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="client-dashboard">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-medium text-[#1A1A1A]">My Bookings</h1>
            <p className="text-stone-500">Welcome back, {user?.name}</p>
          </div>
          <Link to="/discover">
            <Button className="btn-primary" data-testid="browse-services-btn">Browse Services</Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-8">
          <div className="card-surface p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-[#F9F6F0] flex items-center justify-center">
                <Calendar className="h-5 w-5 text-[#D4AF37]" />
              </div>
              <span className="text-stone-500">Upcoming</span>
            </div>
            <p className="text-3xl font-semibold text-[#1A1A1A]" data-testid="upcoming-count">{upcomingBookings.length}</p>
          </div>
          <div className="card-surface p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-[#F9F6F0] flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <span className="text-stone-500">Completed</span>
            </div>
            <p className="text-3xl font-semibold text-[#1A1A1A]">{completedBookings.length}</p>
          </div>
          <div className="card-surface p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-[#F9F6F0] flex items-center justify-center">
                <Star className="h-5 w-5 text-[#D4AF37]" />
              </div>
              <span className="text-stone-500">Reviews Given</span>
            </div>
            <p className="text-3xl font-semibold text-[#1A1A1A]">{Object.values(reviewEligibility).filter(e => e.client_review_submitted).length}</p>
          </div>
        </div>

        {/* Referral widget */}
        <ReferralCard />

        {/* Recently Viewed */}
        {recentlyViewed.length > 0 && (
          <div className="mb-8" data-testid="recently-viewed-section">
            <div className="flex items-center gap-2 mb-4">
              <Eye className="h-5 w-5 text-[#D4AF37]" />
              <h2 className="font-heading text-xl font-medium text-[#1A1A1A]">Recently Viewed</h2>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-thin">
              {recentlyViewed.map(item => (
                <Link
                  key={item.service_id}
                  to={`/service/${item.service_id}`}
                  className="card-surface p-4 flex-shrink-0 w-[220px] hover:shadow-md transition-shadow snap-start"
                  data-testid={`recently-viewed-${item.service_id}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center text-white text-sm font-heading overflow-hidden flex-shrink-0">
                      {item.provider_image
                        ? <img src={item.provider_image} alt="" className="w-full h-full object-cover" />
                        : item.provider_name?.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-stone-400 truncate">{item.provider_name}</p>
                      {item.provider_rating > 0 && (
                        <span className="flex items-center gap-0.5 text-xs text-[#D4AF37]">
                          <Star className="h-3 w-3 fill-current" />{item.provider_rating.toFixed(1)}
                        </span>
                      )}
                    </div>
                  </div>
                  <h4 className="font-medium text-[#1A1A1A] text-sm line-clamp-1 mb-1">{item.name}</h4>
                  <div className="flex items-center justify-between text-xs text-stone-500">
                    <span>{formatDuration(item.duration)}</span>
                    <span className="font-semibold text-[#D4AF37]">£{item.price}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div ref={tabsScrollRef} className="mb-8 -mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto scrollbar-hide">
            <TabsList className="inline-flex w-max bg-white border border-[#E6E4DD] h-auto p-1">
              <TabsTrigger value="upcoming" className="whitespace-nowrap data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white">
                Upcoming ({upcomingBookings.length})
              </TabsTrigger>
              <TabsTrigger value="completed" className="whitespace-nowrap data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white">
                Completed ({completedBookings.length})
              </TabsTrigger>
              <TabsTrigger value="cancelled" className="whitespace-nowrap data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white">
                Cancelled ({cancelledBookings.length})
              </TabsTrigger>
              <TabsTrigger value="favourites" className="whitespace-nowrap data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="favourites-tab-trigger">
                <Heart className="h-4 w-4 mr-1" />
                Favourites
              </TabsTrigger>
              <TabsTrigger value="payment-methods" className="whitespace-nowrap data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="payment-methods-tab-trigger">
                <CreditCard className="h-4 w-4 mr-1" />
                Payment methods
              </TabsTrigger>
              <TabsTrigger value="feedback" className="whitespace-nowrap data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="feedback-tab-trigger" style={betaConfig?.beta_mode ? { display: 'none' } : undefined}>
                <Sparkles className="h-4 w-4 mr-1" />
                Feature requests
              </TabsTrigger>
              <TabsTrigger value="security" className="whitespace-nowrap data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="security-tab-trigger">
                <ShieldCheck className="h-4 w-4 mr-1" />
                Security
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="upcoming">
            {upcomingBookings.length === 0 ? (
              <div className="card-surface p-6 sm:p-12 text-center">
                <Calendar className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-medium text-[#1A1A1A] mb-2">No upcoming bookings</h3>
                <p className="text-stone-500 mb-6">Browse our services and book your next appointment.</p>
                <Link to="/discover"><Button className="btn-primary">Discover Services</Button></Link>
              </div>
            ) : (
              <>
              <div className="space-y-4" data-testid="upcoming-bookings">
                {(showAllUpcoming ? upcomingBookings : upcomingBookings.slice(0, LIST_LIMIT)).map((booking) => (
                  <BookingCard key={booking.booking_id} booking={booking} variant="upcoming" onOpenConsultation={openConsultationForm} onBookingUpdated={fetchBookings} />
                ))}
              </div>
              {upcomingBookings.length > LIST_LIMIT && (
                <div className="mt-4 text-center">
                  <Button variant="outline" onClick={() => setShowAllUpcoming(!showAllUpcoming)} className="border-[#E6E4DD] text-stone-600" data-testid="upcoming-view-all-btn">
                    {showAllUpcoming ? 'Show Less' : `View All ${upcomingBookings.length} Bookings`}
                  </Button>
                </div>
              )}
              </>
            )}
          </TabsContent>

          <TabsContent value="completed">
            {completedBookings.length === 0 ? (
              <div className="card-surface p-6 sm:p-12 text-center">
                <CheckCircle className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-medium text-[#1A1A1A] mb-2">No completed bookings yet</h3>
                <p className="text-stone-500">Your completed appointments will appear here.</p>
              </div>
            ) : (
              <>
              <div className="space-y-4" data-testid="completed-bookings">
                {(showAllCompleted ? completedBookings : completedBookings.slice(0, LIST_LIMIT)).map((booking) => {
                  const elig = reviewEligibility[booking.booking_id];
                  const alreadyReviewed = elig?.client_review_submitted;
                  const canReview = elig?.can_review;
                  const hoursLeft = elig?.hours_remaining;

                  let reviewSlotContent;
                  if (alreadyReviewed) {
                    reviewSlotContent = (
                      <span className="text-xs text-green-600 flex items-center gap-1" data-testid={`reviewed-badge-${booking.booking_id}`}>
                        <CheckCircle className="h-3.5 w-3.5" />Reviewed
                      </span>
                    );
                  } else if (canReview) {
                    reviewSlotContent = (
                      <ReviewDialog
                        open={reviewDialogOpen && selectedBooking?.booking_id === booking.booking_id}
                        onOpenChange={(open) => {
                          setReviewDialogOpen(open);
                          if (open) setSelectedBooking(booking);
                        }}
                        booking={booking}
                        rating={reviewRating}
                        setRating={setReviewRating}
                        reviewText={reviewText}
                        setReviewText={setReviewText}
                        onSubmit={handleReview}
                        reviewType="client_to_provider"
                      />
                    );
                  } else if (hoursLeft) {
                    reviewSlotContent = (
                      <ReviewDialog
                        booking={booking}
                        disabled
                        disabledReason={`${Math.ceil(hoursLeft)}h until review`}
                      />
                    );
                  } else {
                    reviewSlotContent = null;
                  }

                  return (
                    <BookingCard
                      key={booking.booking_id}
                      booking={booking}
                      variant="completed"
                      reviewSlot={reviewSlotContent}
                      autoOpenReport={reportBookingParam === booking.booking_id}
                      onBookingUpdated={fetchBookings}
                    />
                  );
                })}
              </div>
              {completedBookings.length > LIST_LIMIT && (
                <div className="mt-4 text-center">
                  <Button variant="outline" onClick={() => setShowAllCompleted(!showAllCompleted)} className="border-[#E6E4DD] text-stone-600" data-testid="completed-view-all-btn">
                    {showAllCompleted ? 'Show Less' : `View All ${completedBookings.length} Bookings`}
                  </Button>
                </div>
              )}
              </>
            )}
          </TabsContent>

          <TabsContent value="cancelled">
            {cancelledBookings.length === 0 ? (
              <div className="card-surface p-6 sm:p-12 text-center">
                <XCircle className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-medium text-[#1A1A1A] mb-2">No cancelled bookings</h3>
                <p className="text-stone-500">Cancelled appointments will appear here.</p>
              </div>
            ) : (
              <>
              <div className="space-y-4">
                {(showAllCancelled ? cancelledBookings : cancelledBookings.slice(0, LIST_LIMIT)).map((booking) => (
                  <BookingCard key={booking.booking_id} booking={booking} variant="cancelled" />
                ))}
              </div>
              {cancelledBookings.length > LIST_LIMIT && (
                <div className="mt-4 text-center">
                  <Button variant="outline" onClick={() => setShowAllCancelled(!showAllCancelled)} className="border-[#E6E4DD] text-stone-600" data-testid="cancelled-view-all-btn">
                    {showAllCancelled ? 'Show Less' : `View All ${cancelledBookings.length} Bookings`}
                  </Button>
                </div>
              )}
              </>
            )}
          </TabsContent>

          <TabsContent value="favourites" data-testid="favourites-tab-content">
            <Favourites />
          </TabsContent>

          <TabsContent value="payment-methods" data-testid="payment-methods-tab-content">
            <PaymentMethodsSection />
          </TabsContent>

          <TabsContent value="feedback" data-testid="feedback-tab-content">
            {!betaConfig?.beta_mode && (
              <FeatureRequestsSection helperText="Tell us what would make Zuriya more useful for you. Upvote what other clients have asked for, so we know what to build next." />
            )}
          </TabsContent>

          <TabsContent value="security" data-testid="security-tab-content">
            <div className="space-y-6">
              <TwoFactorSettingsCard />
              <NotificationPreferencesCard />
              <RecognisedDevicesCard />
              <DeleteAccountCard role="client" />
            </div>
          </TabsContent>
        </Tabs>

        <ConsultationFormDialog
          open={consultationDialogOpen}
          onOpenChange={setConsultationDialogOpen}
          consultationForm={consultationForm}
          consultationBooking={consultationBooking}
          formAnswers={formAnswers}
          setFormAnswers={setFormAnswers}
          onSubmit={submitConsultation}
        />
      </div>
    </div>
  );
}
